// Import actions needed for refreshFeedData
import { getClearData, getUserInfo, getGroupPosts } from "../redux/actions";
import avatars from "../assets/avatars";
import { getActiveCommunityFeedOrder } from "../utils/communityFeedOrder";
import { postBodyHtml } from "../utils/postBodyHtml";

const dayjs = require("dayjs");
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
const customParseFormat = require("dayjs/plugin/customParseFormat");
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export function areArraysEquals(arr1, arr2) {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return false;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }

  return true;
}

export function addOneDayToDateString(dateString) {
  return dayjs(dateString).add(1, "day").format("YYYY-MM-DD");
}
export function subtractOneDayToDateString(dateString) {
  return dayjs(dateString).subtract(1, "day").format("YYYY-MM-DD");
}

export function synchronizeDatetoDate(date) {
  if (!(date instanceof Date)) {
    throw new Error("The param must be an instance of Date");
  }

  const fechaRoma = dayjs(date).utc().tz("Europe/Rome");
  const fechaFormateada = fechaRoma.format("YYYY-MM-DD HH:mm:ss");

  return new Date(fechaFormateada);
}

export function synchronizeDatetoString(date) {
  if (!(date instanceof Date)) {
    throw new Error("The param must be an instance of Date");
  }

  const formattedDate = dayjs(date).startOf("day").format("YYYY-MM-DD");

  return formattedDate;
}

export function synchronizeDatetoFullString(date) {
  if (!(date instanceof Date)) {
    throw new Error("El parámetro debe ser una instancia de Date");
  }

  const formattedDate = dayjs(date).format("YYYY-MM-DD HH:mm:ss");

  return formattedDate;
}

export function stringDatetoDate(dateString) {
  const formattedDate = `${dateString}T00:00:00Z`;

  const date = new Date(formattedDate);

  return date;
}

export function stringDateIsLess(fecha1, fecha2) {
  const fechaObj1 = new Date(fecha1);
  const fechaObj2 = new Date(fecha2);
  return fechaObj1 < fechaObj2;
}

export function dateIsLess(fecha1, fecha2) {
  return fecha1 < fecha2;
}

export function todayDate() {
  return synchronizeDatetoDate(new Date());
}

export function todayStringDate() {
  return synchronizeDatetoString(new Date());
}

export function todayFullStringDate() {
  return synchronizeDatetoFullString(new Date());
}

// const fechaArray = [
//   ['2023-06-11', '2023-06-17'],
//   ['2023-06-25', '2023-07-01'],
//   ['2023-07-09', '2023-07-15']
// ]

export function getNotIncludedDates(dateRanges) {
  const parsedDates = [];

  for (let i = 0; i < dateRanges.length - 1; i++) {
    const end = dayjs(dateRanges[i][1]);
    const start = dayjs(dateRanges[i + 1][0]);

    if (end.isValid() && start.isValid() && end.isBefore(start)) {
      let current = end.add(1, "day");

      while (current.isBefore(start)) {
        parsedDates.push(current.format("YYYY-MM-DD"));
        current = current.add(1, "day");
      }
    }
  }

  return parsedDates;
}

export function orderDatesSubArrays(dietRoute1, dietRoute2) {
  if (dietRoute1[0] < dietRoute2[0]) return -1;
  if (dietRoute1[0] > dietRoute2[0]) return 1;
  return 0;
}

export function substractOneDayToEndDatesSubArrays([, end], index, datesArray) {
  datesArray[index][1] = dayjs(end).subtract(1, "day").format("YYYY-MM-DD");
}

export function isValidEMail(email) {
  return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
}

export function getStep(route) {
  const parts = route.split("/");
  const lastPart = parts.pop();

  return parseInt(lastPart);
}

export const randId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Splits a backend quantity string like "2 cucchiai (20g)" into its household
 * measure and the indicative gram value.
 *   "2 cucchiai (20g)" -> { household: "2 cucchiai", grams: "20 g" }
 *   "6 (12g)"          -> { household: "6", grams: "12 g" }
 *   "1 tazza"          -> { household: "1 tazza", grams: null }
 */
export function parseIndicativeQuantity(quantity) {
  const raw = String(quantity ?? "").trim();
  if (!raw) return { household: "", grams: null };
  const m = raw.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  if (!m) return { household: raw, grams: null };
  const household = m[1].trim() || raw;
  const grams = m[2].trim().replace(/(\d)\s*g\b/i, "$1 g") || null;
  return { household, grams };
}

export function isValidName(name) {
  return /^(([a-zA-Z]|[À-ÖØ-öø-ÿ Ÿœ]){3,30})$/.test(name);
}

export function isValidNickname(nickname) {
  return /^([\w.-]){4,30}$/.test(nickname);
}

export function isValidGender(sex) {
  return ["male", "female"].includes(sex);
}

export function getMinimumLegalAgeDate() {
  const fechaActual = new Date();
  const fechaMinimaMayorEdad = new Date(
    fechaActual.getFullYear() - 18,
    fechaActual.getMonth(),
    fechaActual.getDate() + 1,
  );
  const date = fechaMinimaMayorEdad.toISOString().substring(0, 10);
  return date;
}

/**
 * The `max` for a date-of-birth wheel: the end of the latest year a member of
 * age can be born in. With the exact 18th-birthday date as `max`, the wheel
 * drops that year's later months and days: it opens in that year, so a member
 * born in October could not reach their month or day until they had changed
 * the year first (client, 17/09/2026). The exact date is checked on save
 * instead.
 */
export function getBirthDateWheelMax() {
  return `${getMinimumLegalAgeDate().slice(0, 4)}-12-31`;
}

export function dataURLtoBlob(dataURL) {
  const arr = dataURL.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export function getMondayDate(date) {
  const dateObj = new Date(date);

  const dayOfWeek = dateObj.getDay();

  if (dayOfWeek === 0) {
    dateObj.setDate(dateObj.getDate() - 6);
  } else {
    dateObj.setDate(dateObj.getDate() - dayOfWeek + 1);
  }

  return dateObj.toISOString().slice(0, 10);
}

export const daysOfTheWeek = [
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

export const monthsOfTheYear = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

export function getWeekDays(date) {
  const dateObj = dayjs(getMondayDate(date));

  const daysOfWeek = [
    dateObj,
    dateObj.add(1, "day"),
    dateObj.add(2, "day"),
    dateObj.add(3, "day"),
    dateObj.add(4, "day"),
    dateObj.add(5, "day"),
    dateObj.add(6, "day"),
  ];

  const formattedDaysOfWeek = daysOfWeek.map((day) => {
    return { day: day.date(), month: day.month(), year: day.year() };
  });

  return formattedDaysOfWeek;
}

export function getDayAsObject(date) {
  const dateObj = dayjs(date);
  return { day: dateObj.date(), month: dateObj.month(), year: dateObj.year() };
}

export function getWeekIndex(date) {
  const dateObj = dayjs(date);

  const dayOfWeek = dateObj.day();
  if (dayOfWeek === 6) {
    return 0;
  } else {
    return dayOfWeek + 1;
  }
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/* USER AGENT FUNCTIONS */

export function _isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

export function _isIOS() {
  return /iPhone|iPad|iPod/.test(window.navigator.userAgent);
}

// Expresiones regulares para navegadores populares
const desktopBrowsers = [
  /Chrome/,
  /Firefox/,
  /Safari/,
  /Edge/,
  /Opera/,
  /MSIE/,
  /Brave/,
];

const mobileBrowsers = [
  /SamsungBrowser/,
  /Mobile.*Safari/,
  /Android/,
  /Mobile.*Chrome|CriOS/, // Agregamos CriOS, que es común en agentes de usuario de Chrome en iOS
  /Brave/,
];

// Verificar el agente de usuario al inicio de la ejecución
const userAgent = navigator.userAgent;

export function _detectBrowser() {
  // Verify if the user is using a mobile device
  const isMobile = /Mobile/.test(userAgent);

  let browser = "Unknown";
  let browserType = "Unknown";

  if (isMobile) {
    browserType = "Mobile";
    mobileBrowsers.some((regex) => {
      if (regex.test(userAgent)) {
        browser = regex.toString().match(/\/(.*)\//)[1];
        return true; // Break the loop
      }
      return false;
    });
  } else {
    browserType = "Desktop";
    desktopBrowsers.some((regex) => {
      if (regex.test(userAgent)) {
        browser = regex.toString().match(/\/(.*)\//)[1];
        return true; // Break the loop
      }
      return false;
    });
  }

  return { browser, browserType };
}

/* USER AGENT FUNCTIONS */

export function _isStandalone() {
  if ("serviceWorker" in navigator && "caches" in window) {
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    ) {
      return true;
    }
  }

  return false;
}

export async function _isDietaSocialInstalled() {
  try {
    const relatedApps = await navigator.getInstalledRelatedApps();
    return relatedApps.length > 0;
  } catch (error) {
    console.error("Error:", error);
    return false;
  }
}

export function clearCache(event) {
  console.log("Cache clear called");

  // Complete the refresh event
  if (event?.detail?.complete) {
    setTimeout(() => {
      event.detail.complete();
    }, 1000);
  }
}

// New function for feed refresh
/**
 * Rebuild page one of a feed.
 *
 * `order_by` defaults to whatever the Community feed currently has selected
 * rather than to nothing. It used to be omitted entirely, and the API reads a
 * missing order as "Nuovi post" — so a pull-to-refresh silently re-sorted the
 * list by publication date while the selector still read "Attività recente",
 * and a post that had just been bumped by a new comment dropped straight back
 * down. The page-one reducer replaces the whole list, so nothing corrected it
 * afterwards.
 */
export function refreshFeedData(
  dispatch,
  from_main_group = true,
  group_id = null,
  order_by = getActiveCommunityFeedOrder(),
) {
  console.log("Refreshing feed data...");

  // Clear localStorage cache to force update
  localStorage.removeItem("adoc_last_update");

  // Dispatch actions to update data
  dispatch(getClearData());
  dispatch(getUserInfo());

  // Refresh feed posts
  dispatch(
    getGroupPosts({
      is_fixed: false,
      page: 1,
      from_main_group,
      group_id,
      order_by,
      refresh: true, // Flag to indicate it's a refresh
    }),
  );

  // Refresh liked posts for "preferiti" tab
  dispatch(
    getGroupPosts({
      is_liked: true,
      page: 1,
      from_main_group,
      group_id,
      order_by,
      refresh: true,
    }),
  );
}

export function checkOverflow(titleElementRef) {
  if (titleElementRef.current) {
    const isOverflowing =
      titleElementRef.current.scrollWidth > titleElementRef.current.clientWidth;

    if (isOverflowing) {
      titleElementRef.current.classList.add("banner-title");
      titleElementRef.current.classList.remove("ellipsis-title");
    } else {
      titleElementRef.current.classList.add("ellipsis-title");
      titleElementRef.current.classList.remove("banner-title");
    }
  }
}

export function upperFirst(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Wrap each @nickname the post author explicitly mentioned in a clickable
 * span (post render is an HTML string via dangerouslySetInnerHTML, so we can't
 * use React nodes like the comment renderer). The span calls the global
 * window.handleMentionClick — mirrors how processPostHTML wires links to
 * window.handleButtonLink. `mentions` is [{ id, nickname }, ...] from the post.
 */
export const injectPostMentions = (text, mentions) => {
  if (!text || !Array.isArray(mentions) || !mentions.length) return text;
  let out = text;
  // Longest handles first so a short nickname that prefixes a longer one
  // doesn't partially match.
  const sorted = [...mentions]
    .filter((m) => m && m.id != null && m.nickname)
    .sort((a, b) => (b.nickname?.length || 0) - (a.nickname?.length || 0));
  for (const m of sorted) {
    const handle = String(m.nickname);
    const esc = handle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // "@handle" not immediately followed by a word char (so @handle doesn't
    // match inside @handleXYZ).
    const re = new RegExp(`@${esc}(?![\\w])`, "g");
    const safe = handle
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    out = out.replace(
      re,
      `<span class="post-mention" onClick="handleMentionClick(this)" data-user-id="${m.id}">@${safe}</span>`,
    );
  }
  return out;
};

export function processPostHTML(text) {
  if (!text) return "";

  // Links, emoji pictures and paragraph breaks: see utils/postBodyHtml.
  return <div dangerouslySetInnerHTML={{ __html: postBodyHtml(text) }} />;
}

export const NotificationService = (() => {
  let _permission = "default";

  const requestPermission = async () => {
    _permission = await Notification.requestPermission();
  };

  const getPermission = () => _permission;

  return {
    requestPermission,
    getPermission,
  };
})();

// Day names for the unlock labels. Index 0 is the shopping list label so the
// existing 0-based lookup in unlockTexts keeps working untouched.
const UNLOCK_LABELS_BY_DAY = [
  "Lista della spesa",
  "Menù del Lunedì",
  "Menù del Martedì",
  "Menù del Mercoledì",
  "Menù del Giovedì",
  "Menù del Venerdì",
  "Menù del Sabato",
  "Menù della Domenica",
];

export const unlockTexts = (unlocks) => {
  let menu_days = unlocks?.menu_days ? [...unlocks.menu_days] : [];
  if (unlocks?.shopping_list) menu_days?.unshift(0);

  return menu_days?.map((day) => UNLOCK_LABELS_BY_DAY[day]).join(", ");
};

/**
 * Structured version of unlockTexts. Preserves the per-item kind/day so the
 * caller can render each label as a tappable link to its underlying menu day
 * or to the shopping list. Order mirrors unlockTexts (shopping list first if
 * unlocked, then days in the API-provided order).
 *
 * Returned shape: [{ kind: "shopping_list" | "menu_day", day: number|null, label: string }]
 */
export const unlockItems = (unlocks) => {
  const items = [];
  if (unlocks?.shopping_list) {
    items.push({
      kind: "shopping_list",
      day: null,
      label: UNLOCK_LABELS_BY_DAY[0],
    });
  }
  for (const raw of unlocks?.menu_days || []) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > 7) continue;
    items.push({
      kind: "menu_day",
      day: n,
      label: UNLOCK_LABELS_BY_DAY[n],
    });
  }
  return items;
};

/**
 * Split module unlocks into contents already available at module start
 * (`shopping_list_at_start` / `menu_days_at_start`) vs contents unlocked
 * only after completing the module. Intersection with the general unlocks
 * list — at_start flags alone never invent extra items.
 *
 * @returns {{ atStart: ReturnType<typeof unlockItems>, remaining: ReturnType<typeof unlockItems> }}
 */
export const splitUnlockItems = (unlocks) => {
  const all = unlockItems(unlocks);
  const shoppingAtStart = unlocks?.shopping_list_at_start === true;
  const atStartDays = new Set(
    (unlocks?.menu_days_at_start || [])
      .map((d) => Number(d))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7),
  );

  const atStart = [];
  const remaining = [];
  for (const it of all) {
    const isAtStart =
      it.kind === "shopping_list"
        ? shoppingAtStart
        : it.kind === "menu_day"
          ? atStartDays.has(it.day)
          : false;
    (isAtStart ? atStart : remaining).push(it);
  }
  return { atStart, remaining };
};

// ---- robust date parsing at local NOON (handles "YYYY-MM-DD" and "YYYY-MM-DD HH:mm:ss") ----
const parseYmdLocalNoon = (input) => {
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); // grab only Y-M-D
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return new Date(y, mo - 1, d, 12, 0, 0, 0); // local time, noon
};

export const getUnlockDateFromSubscription = (
  subscriptionStart,
  subscriptionFromDay,
) => {
  if (!subscriptionStart || subscriptionFromDay == null) return null;
  const base = parseYmdLocalNoon(subscriptionStart);
  if (!base) return null;
  const daysToAdd =
    subscriptionFromDay > 0 ? subscriptionFromDay - 1 : subscriptionFromDay;
  const d = new Date(base);
  d.setDate(base.getDate() + daysToAdd);
  return d;
};

export const isFuture = (d) => {
  if (!d) return false;
  // Compare calendar dates only — the module should be available
  // for the entire unlock day, regardless of time-of-day.
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return targetStart.getTime() > todayStart.getTime();
};

/**
 * Lock rule (tabs/lessons preview):
 *  - If the computed unlock date is in the future => LOCKED (regardless of is_enabled)
 *  - Else if module is explicitly disabled            => LOCKED
 *  - Else                                             => UNLOCKED
 */
export const isPreviewLocked = (mod, subscription) => {
  if (!mod) return false;
  const unlock = getUnlockDateFromSubscription(
    subscription?.start_date,
    mod?.subscription_from_day,
  );
  if (isFuture(unlock)) return true; // unlock date takes precedence
  if (mod.is_enabled === false) return true; // backend hard lock
  return false;
};

/**
 * "Course started" flag — set the first time a user reaches a video-pillola
 * detail page (LessonPage). Because modules are sequential, the first LessonPage
 * a brand-new user can reach is the first video of the first module. Stored per
 * user so it never leaks across accounts (and logout's localStorage.clear()
 * wipes it). Shape: { [userId]: true }.
 */
const COURSE_STARTED_KEY = "ds_course_started";

export const isCourseStarted = (userId) => {
  if (!userId) return false;
  try {
    const all = JSON.parse(localStorage.getItem(COURSE_STARTED_KEY) || "{}");
    return Boolean(all?.[String(userId)]);
  } catch {
    return false;
  }
};

export const markCourseStarted = (userId) => {
  if (!userId) return;
  try {
    const all = JSON.parse(localStorage.getItem(COURSE_STARTED_KEY) || "{}");
    if (all[String(userId)]) return; // idempotent
    all[String(userId)] = true;
    localStorage.setItem(COURSE_STARTED_KEY, JSON.stringify(all));
  } catch {
    /* ignore storage errors */
  }
};

/**
 * Resolve the "first useful content" route for any course-entry CTA
 * (burger "Il tuo Percorso", locked meal-plan "Vai alle video-pillole", etc.).
 * `courseStarted` = the localStorage flag above (user opened the first video).
 *  - no modules / not loaded                      => null  (caller stays on /corso)
 *  - all modules completed                        => "/corso?tab=fine"
 *  - never started (no flag, no backend progress) => null  (caller stays on /corso intro)
 * Otherwise, for the current module (first not-completed):
 *  - no pill completed yet                        => "/corso/tabs?m=<id>"  (module intro)
 *  - >=1 completed and another remains            => "/corso/lezioni/<id>?resume=1"
 *                                                    (LessonsList jumps to first incomplete pill)
 *  - >=1 completed but none remain                => "/corso/tabs?m=<id>"  (module intro)
 */
export const resolveCourseEntry = (modules, allPills, courseStarted = false) => {
  const mods = Array.isArray(modules) ? modules : [];
  if (!mods.length) return null;

  // All modules complete → final page of the percorso
  if (mods.every((m) => m.is_completed)) return "/corso?tab=fine";

  const pills = Array.isArray(allPills) ? allPills : [];

  // "Started" = our flag OR any real backend progress (robust if storage was
  // cleared or the user is on a new device). A truly fresh user stays on /corso.
  const started =
    courseStarted ||
    mods.some((m) => m.is_completed) ||
    pills.some((p) => p.is_completed);
  if (!started) return null;

  // Current module = first not-yet-completed module
  const current = mods.find((m) => !m.is_completed);
  if (!current) return "/corso?tab=fine"; // defensive

  const pillsOfCurrent = pills.filter((p) => p.lesson_id === current.id);
  const anyCompleted = pillsOfCurrent.some((p) => p.is_completed);
  const anyIncomplete = pillsOfCurrent.some((p) => !p.is_completed);

  // Completed >=1 and a next pill remains → next pill to complete.
  if (anyCompleted && anyIncomplete) {
    return `/corso/lezioni/${current.id}?resume=1`;
  }

  // First pill not completed yet, OR nothing left to complete → module intro.
  return `/corso/tabs?m=${current.id}`;
};

/**
 * Where "Vai alla prima video-pillola da completare" should go.
 *
 * Used by the locked-pillola modal: the pillola the user needs is often in an
 * EARLIER module (that is precisely why the one they tapped is locked), so this
 * scans modules in order rather than staying inside the current one.
 *
 * Returns a `?resume=1` route rather than a specific pillola on purpose. The
 * order pillole are shown in comes from the multimedia-posts payload, which only
 * the lessons list has; `resume=1` makes that page jump to its own first
 * incomplete row, so the two can never disagree.
 *
 * @returns {string|null} route, or null when there is nothing left to complete
 */
export const resolveFirstIncompletePillTarget = (modules, allPills) => {
  const mods = Array.isArray(modules) ? modules : [];
  const pills = Array.isArray(allPills) ? allPills : [];
  if (!mods.length) return null;

  for (const mod of mods) {
    const pillsOfModule = pills.filter((p) => p.lesson_id === mod.id);

    // Pills for this module haven't loaded — trust the module's own flag rather
    // than skipping over it and sending the user too far forward.
    if (!pillsOfModule.length) {
      if (mod.is_completed !== true) return `/corso/lezioni/${mod.id}?resume=1`;
      continue;
    }

    if (pillsOfModule.some((p) => !p.is_completed)) {
      return `/corso/lezioni/${mod.id}?resume=1`;
    }
  }

  return null;
};

export const getGroupParams = (state, group_id) => {
  const { main, not_main } = state.groups.getGroupPosts;
  const { fixed: main_fixed, general: main_general } = main;
  const { fixed: not_main_fixed, general: not_main_general } = not_main;

  const groups = [main_fixed, main_general, not_main_fixed, not_main_general];

  const foundGroup = groups.find(
    (group) => group?.data?.group?.id === Number(group_id),
  );

  return foundGroup ? foundGroup.data.group : {};
};

export const rotateImage = (image, orientation) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  let width = image.width;
  let height = image.height;

  // if (orientation === 6 || orientation === 8) {
  // 	canvas.width = height
  // 	canvas.height = width
  // } else {
  // 	canvas.width = width
  // 	canvas.height = height
  // }

  canvas.width = width;
  canvas.height = height;

  switch (orientation) {
    case 2:
      // Flip image horizontally - The image is mirrored along its vertical axis.
      ctx.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      // Rotate image 180 degrees - The image is upside down.
      ctx.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      // Flip image vertically - The image is mirrored along its horizontal axis.
      ctx.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      // Rotate 90 degrees clockwise and flip vertically - This is a combination of rotating the image 90 degrees to the right and then flipping it along its horizontal axis.
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    // case 6:
    // 	// Rotate 90 degrees clockwise - The image was captured with the camera rotated 90 degrees from its normal orientation, typically this means the device was held vertically. This is common for portrait photos.
    // 	ctx.transform(0, 1, -1, 0, height, 0)
    // 	break
    case 7:
      // Rotate 90 degrees clockwise and flip horizontally - Similar to case 5, but the image is flipped along its vertical axis after being rotated.
      ctx.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      // Rotate 90 degrees counterclockwise - The image was captured with the camera rotated 270 degrees from its normal orientation, or 90 degrees to the left. This could happen if the device was held upside down vertically.
      ctx.transform(0, -1, 1, 0, 0, width);
      break;
    default:
      // No transformation needed - The image is in its normal orientation, with no rotation or flipping required.
      break;
  }

  ctx.drawImage(image, 0, 0, width, height);

  const rotatedImage = new Image();
  rotatedImage.src = canvas.toDataURL("image/jpeg", 1.0);

  return rotatedImage;
};

/**
 * Reads a File's EXIF orientation and returns a NEW File with the pixels
 * physically rotated to upright (orientation reset to 1). Unlike rotateImage()
 * above, this correctly handles orientation 6/8 (the common phone-portrait
 * cases) by swapping the canvas dimensions — so iPhone photos no longer upload
 * rotated 90°. Falls back to the original file on any error / unknown
 * orientation so uploads never break.
 */
export const correctImageOrientation = (file) =>
  new Promise((resolve) => {
    // Lazy require so exif-js only loads when a photo is actually picked.
    let EXIF;
    try {
      // eslint-disable-next-line global-require
      EXIF = require("exif-js");
    } catch {
      resolve(file);
      return;
    }
    if (!file || !file.type?.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => resolve(file);
      img.onload = () => {
        try {
          EXIF.getData(img, function () {
            const orientation = EXIF.getTag(this, "Orientation") || 1;
            if (orientation === 1) {
              resolve(file);
              return;
            }
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const w = img.width;
            const h = img.height;
            if (orientation >= 5 && orientation <= 8) {
              canvas.width = h;
              canvas.height = w;
            } else {
              canvas.width = w;
              canvas.height = h;
            }
            switch (orientation) {
              case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;
              case 3: ctx.transform(-1, 0, 0, -1, w, h); break;
              case 4: ctx.transform(1, 0, 0, -1, 0, h); break;
              case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
              case 6: ctx.transform(0, 1, -1, 0, h, 0); break;
              case 7: ctx.transform(0, -1, -1, 0, h, w); break;
              case 8: ctx.transform(0, -1, 1, 0, 0, w); break;
              default: break;
            }
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(file);
                  return;
                }
                resolve(
                  new File([blob], file.name || "photo.jpg", {
                    type: "image/jpeg",
                  }),
                );
              },
              "image/jpeg",
              0.92,
            );
          });
        } catch {
          resolve(file);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

/**
 * Rotate an image File 90° (clockwise by default; counter-clockwise for
 * direction === "left") and return a NEW upright JPEG File. Mirrors the manual
 * rotate control in the post composer, but File→File so the comment and avatar
 * flows (which keep the picked image as a File) can share it. Falls back to the
 * original File on any decode/encode error so uploads never break.
 */
export const rotateImageFile90 = (file, direction = "right") =>
  new Promise((resolve) => {
    if (!file) {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };
    const img = new Image();
    img.onerror = () => {
      cleanup();
      resolve(file);
    };
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const canvas = document.createElement("canvas");
        // 90° rotation swaps width/height.
        canvas.width = h;
        canvas.height = w;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(((direction === "left" ? -90 : 90) * Math.PI) / 180);
        ctx.drawImage(img, -w / 2, -h / 2);
        canvas.toBlob(
          (blob) => {
            cleanup();
            resolve(
              blob
                ? new File([blob], file.name || "photo.jpg", {
                    type: "image/jpeg",
                  })
                : file,
            );
          },
          "image/jpeg",
          0.92,
        );
      } catch {
        cleanup();
        resolve(file);
      }
    };
    img.src = url;
  });

/**
 * Draw an image URL (data URL, blob:, or Capacitor webPath) onto a canvas and
 * export it as a JPEG File. Guarantees a valid `image/jpeg` upload regardless of
 * the source format — iPhone library photos are often HEIC and iOS blobs can
 * carry an empty MIME type, both of which the upload backend rejects. Rejects on
 * decode/encode failure so the caller can surface an error.
 */
export const imageUrlToJpegFile = (url, filename = "photo.jpg", quality = 0.9) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Image encode failed"));
              return;
            }
            resolve(new File([blob], filename, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality,
        );
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Image decode failed"));
    img.src = url;
  });

/**
 * Downscale an image File to a bounded JPEG (longest side ≤ maxDim) for fast,
 * reliable uploads. Pair with correctImageOrientation() first so the result is
 * upright. Returns the original File when it's already small enough or on any
 * error, so uploads never break.
 */
export const downscaleImageFile = (
  file,
  { maxDim = 1024, quality = 0.85 } = {},
) =>
  new Promise((resolve) => {
    if (!file || !file.type?.startsWith("image/")) {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const longest = Math.max(img.width, img.height);
        if (longest <= maxDim) {
          resolve(file);
          return;
        }
        const scale = maxDim / longest;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) =>
            resolve(
              blob
                ? new File(
                    [blob],
                    (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg",
                    { type: "image/jpeg" },
                  )
                : file,
            ),
          "image/jpeg",
          quality,
        );
      } catch {
        resolve(file);
      }
    };
    img.src = url;
  });

export const decodeHtmlEntities = (str) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

export const isEmptyObj = (obj) => {
  if (!obj) return true;
  return Object.keys(obj).length === 0;
};

// Generic helper to resolve avatar references (local or remote)
// Supports many backend formats:
// - avatar01 / avatar1
// - avatar-01 / avatar_01
// - avatar01.jpg / avatar-01.png
// - https://.../avatar-01.jpg?token=...
// If it's not a predefined avatar, URLs are returned as-is.
export const resolveAvatarImage = (avatarRef) => {
  if (!avatarRef || typeof avatarRef !== "string") return null;

  const ref = avatarRef.trim();
  if (!ref) return null;

  const normalizeAvatarKey = (input) => {
    if (!input || typeof input !== "string") return null;

    // Remove query/hash and keep only the last path segment
    const noQuery = input.split(/[?#]/)[0] || "";
    const lastSegment = (noQuery.split("/").pop() || "").trim();
    if (!lastSegment) return null;

    // Strip extension + common suffixes (e.g. @2x)
    let base = lastSegment
      .replace(/\.(jpg|jpeg|png|gif|webp)$/i, "")
      .replace(/@\d+x$/i, "")
      .toLowerCase();

    // Match avatar variants (avatar01, avatar-01, avatar_01, avatar1)
    const m = base.match(/avatar[^0-9]*0*(\d{1,2})$/i);
    if (!m) return null;

    const n = String(parseInt(m[1], 10));
    if (!n || Number.isNaN(Number(n))) return null;
    return `avatar${n.padStart(2, "0")}`;
  };

  const tryLocalFromKey = (key) => {
    const normalized = normalizeAvatarKey(key);
    if (!normalized) return null;
    return avatars?.avatarsByNumber?.[normalized] ?? null;
  };

  // 1) Local key / filename (no protocol): treat as predefined avatar ref
  if (!/^https?:\/\//i.test(ref)) {
    return tryLocalFromKey(ref);
  }

  // 2) URL: inspect the last path segment for a predefined avatar name
  try {
    const local = tryLocalFromKey(ref);
    if (local) return local;
  } catch (e) {
    // Ignore and fall back
  }

  // 3) Not a predefined avatar: use remote URL as-is
  return ref;
};

/**
 * True for the backend's generic "no photo" asset (/images/placeholder.svg,
 * usually with a mix ?id= cache-buster). It means the user has no avatar, so
 * callers should fall through to their own placeholder (initials, DS logo)
 * rather than render an anonymous grey square.
 */
export const isPlaceholderAvatarUrl = (value) =>
  typeof value === "string" &&
  /\/images\/placeholder(\.[a-z0-9]+)*\.svg(\?|$)/i.test(value.trim());

// Resolve a user avatar "src" in one place.
// - Accepts either a user object (recommended) or a raw string.
// - Always runs through resolveAvatarImage() so predefined avatars work everywhere.
// - If it's an uploaded filename, it prefixes storage_path when available.
export const resolveUserAvatarSrc = (userLike, opts = {}) => {
  if (!userLike) return null;

  // Allow passing a raw avatar ref directly
  const value =
    typeof userLike === "string"
      ? userLike
      : userLike?.avatar ??
        userLike?.image ??
        userLike?.avatar_url ??
        userLike?.avatarUrl ??
        userLike?.profile_image ??
        userLike?.profileImage ??
        userLike?.picture ??
        userLike?.photo ??
        null;

  if (!value || typeof value !== "string") return null;
  const v = value.trim();
  if (!v) return null;
  // The backend's `avatar_url` accessor returns its own generic
  // /images/placeholder.svg when a user has no photo. That is a perfectly valid
  // URL, so it used to be returned as-is and short-circuited every caller's
  // "no avatar" branch — the user got an anonymous grey glyph instead of their
  // initials. Treat it as absent so the initials / DS-pictogram fallbacks run.
  if (isPlaceholderAvatarUrl(v)) return null;

  // 1) If it's a predefined avatar (key OR URL that ends with avatar-xx.jpg), this returns a local asset.
  const maybeLocalOrUrl = resolveAvatarImage(v);
  if (maybeLocalOrUrl) {
    // If v is already a URL, resolveAvatarImage() returns either the same URL or a local asset.
    if (/^https?:\/\//i.test(v)) return maybeLocalOrUrl;
    // If v is a local avatar key, maybeLocalOrUrl is the bundled asset url.
    if (maybeLocalOrUrl !== v) return maybeLocalOrUrl;
  }

  // 2) If already a URL/data/blob, return as-is.
  if (/^(https?:)?\/\//i.test(v) || /^data:/i.test(v) || /^blob:/i.test(v)) {
    return v;
  }

  // 3) Otherwise it's likely an uploaded filename -> prefix storage path if we have one.
  const storagePath =
    opts?.storagePath ?? userLike?.storage_path ?? userLike?.storagePath ?? "";

  if (storagePath && typeof storagePath === "string") {
    const base = storagePath.trim();
    if (base) {
      const needsSlash = !base.endsWith("/") && !v.startsWith("/");
      const cleanedV = base.endsWith("/") ? v.replace(/^\/+/, "") : v;
      return needsSlash ? `${base}/${cleanedV}` : `${base}${cleanedV}`;
    }
  }

  // 4) Last resort: return the raw value
  return v;
};

/**
 * Light auto-formatting for user-written text (posts + comments): ensure a
 * space after . , ! ? and capitalize the first letter of the text and of each
 * new sentence/line. Applied on blur / at submit — never per keystroke, so it
 * can't fight the caret while typing. Idempotent.
 */
export const autoFormatText = (raw) => {
  if (!raw) return raw;
  let s = String(raw);
  // space after . , ! ? when immediately followed by a non-space, non-punct char
  s = s.replace(/([.,!?])(?=[^\s.,!?])/g, "$1 ");
  // capitalize the very first letter
  s = s.replace(/^(\s*)(\p{Ll})/u, (m, ws, ch) => ws + ch.toUpperCase());
  // capitalize the first letter after a sentence end (. ! ?) or a line break
  s = s.replace(
    /([.!?]\s+|\n[ \t]*)(\p{Ll})/gu,
    (m, sep, ch) => sep + ch.toUpperCase(),
  );
  return s;
};

/**
 * "AP" from "Alessandra Pizzarini". Takes first + last word initials.
 *
 * ⚠️ Members routinely put a nickname in brackets or quotes — "Marina Marchini
 * (Mima)" — and the last "word" is then "(Mima)", whose first character is the
 * bracket. That put "M(" on the avatar. Punctuation is trimmed from both ends
 * of every word and anything left empty is dropped, so the initials always come
 * from the name itself. Latin-1 accents are kept: Italian names need them.
 */
export const getInitials = (...parts) => {
  const words = parts
    .filter(Boolean)
    .join(" ")
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/^[^A-Za-zÀ-ÿ0-9]+|[^A-Za-zÀ-ÿ0-9]+$/g, ""))
    .filter(Boolean);
  if (!words.length) return "";
  const first = words[0][0] || "";
  const last = words.length > 1 ? words[words.length - 1][0] || "" : "";
  return (first + last).toUpperCase();
};

/**
 * Avatar fallback for users with no profile photo: their initials on a solid
 * circle, returned as an SVG data-URI so it drops straight into any <img src>
 * (feed, comments, notification rows) without a new component. The colour is
 * derived from the initials so a given user always gets the same one.
 * Returns null when we have no name to work with — callers keep their old
 * fallback in that case.
 */
export const initialsAvatarDataUri = (...nameParts) => {
  const initials = getInitials(...nameParts);
  if (!initials) return null;
  const palette = [
    "#4C9F70", "#2E7DAF", "#8E5BA6", "#C0563F",
    "#B08B2E", "#3F7F7A", "#A0527C", "#5B6BAF",
  ];
  let hash = 0;
  for (let i = 0; i < initials.length; i += 1) {
    hash = (hash * 31 + initials.charCodeAt(i)) >>> 0;
  }
  const bg = palette[hash % palette.length];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">` +
    `<rect width="80" height="80" rx="40" fill="${bg}"/>` +
    `<text x="50%" y="50%" dy=".35em" text-anchor="middle" ` +
    `font-family="Helvetica,Arial,sans-serif" font-size="32" font-weight="600" ` +
    `fill="#ffffff">${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

/**
 * How long a picked clip runs, in seconds.
 *
 * Read from the file itself rather than trusted from anywhere: the Storie
 * composer refuses a clip over the cap, the comment composer does the same, and
 * both need the number before they upload anything.
 *
 * Rejects when the browser cannot decode the file at all, which is the useful
 * signal that the format is not one we can play back either.
 */
export const readVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const probe = document.createElement("video");
    probe.preload = "metadata";

    const done = (value) => {
      URL.revokeObjectURL(url);
      probe.onloadedmetadata = null;
      probe.ontimeupdate = null;
      probe.onerror = null;
      resolve(value);
    };

    probe.onloadedmetadata = () => {
      // A clip straight off the camera routinely reports Infinity here: the
      // MP4's duration lives in an atom the browser has not read yet. Seeking
      // far past the end forces it to work the real length out, at which point
      // timeupdate fires with a usable figure. Without this an Android
      // recording measured Infinity and was rejected as "too long".
      if (Number.isFinite(probe.duration)) {
        done(probe.duration);
        return;
      }
      probe.ontimeupdate = () => {
        if (!Number.isFinite(probe.duration)) return;
        probe.ontimeupdate = null;
        probe.currentTime = 0;
        done(probe.duration);
      };
      probe.currentTime = 1e101;
    };

    probe.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable video"));
    };
    probe.src = url;
  });
